import React, { useMemo } from "react";
import { Pagination } from "@pd/pd-ui-kit";
import { useTranslation } from "react-i18next";

import { useGetLocationData, useSetLocationData } from "~/hooks/locationData";
import { getUsersColumns } from "~/pages/Users/model/tableAdapter";
import { USER_OPTIONS_TABLE } from "~/pages/Users/options";
import { UsersTableRowActions } from "~/pages/Users/ui/UsersTableRowActions";
import { usePaginationParams } from "~/shared/hooks/usePaginationParams";
import { getLocalizedSortableApiKeys } from "~/shared/lib/localizedSortField";
import { TableV2, useTableSort } from "~/shared/ui/TableV2";

import s from "~/pages/Users/Users.less";

const TableUserList = ({
  dataUserList,
  isLoadingUserList,
  hasPermission,
  selectedIds,
  onToggleSelect,
}: {
  dataUserList: { items?: { id?: string | number }[]; total?: number };
  isLoadingUserList: boolean;
  hasPermission: (permission: string) => boolean;
  selectedIds: number[];
  onToggleSelect: (record: { id?: string | number; fullName?: string }) => void;
}) => {
  const { t, i18n } = useTranslation();
  const setData = useSetLocationData();
  const { page, count, sortField, sortType } = useGetLocationData();

  const handleEdit = (item: any) => {
    setData("openModal", "Edit", "currentId", item.id);
  };

  const handleView = (item: any) => {
    setData("openChildModal", "View", "currentId", item.id);
  };

  const handleDelete = (item: any) => {
    setData("openModal", "Sure", "currentId", item.id);
  };

  const onRowRestore = (item: any) => {
    setData("openModal", "Restore", "currentId", item.id);
  };

  const sortableApiKeys = useMemo(() => {
    const lang = localStorage.getItem("language");
    return getLocalizedSortableApiKeys(USER_OPTIONS_TABLE, lang);
  }, [i18n.language]);

  const { sortType: normalizedSortType, onSortChange } = useTableSort({
    sortField,
    sortType,
    sortableColumns: sortableApiKeys,
    setData,
  });

  const { handleCount, handlePage } = usePaginationParams();

  const columns = useMemo(() => {
    const lang = localStorage.getItem("language");
    return getUsersColumns(
      t,
      USER_OPTIONS_TABLE,
      sortField,
      sortType,
      USER_OPTIONS_TABLE,
      lang,
    );
  }, [t, sortField, sortType, i18n.language]);

  return (
    <>
      <TableV2
        columns={columns}
        dataSource={dataUserList?.items}
        loading={isLoadingUserList}
        wrapperClassName={s.users_table}
        onRowEdit={hasPermission("user.updateUser") ? handleEdit : undefined}
        onRowDelete={
          hasPermission("users.deleteUser") ? handleDelete : undefined
        }
        rowDecorator={(record: { status?: string }) =>
          record.status === "removed" ? { className: s.rowRemoved } : {}
        }
        /* TODO PASDILER-2428: радиоколонка переноса пользователей временно скрыта до завершения работ в feat/PASDILER-1829. https://task.sotl.group:8443/browse/PASDILER-2428
        rowControlPrefix={(record) => (
          <RowSelectionControl
            checked={selectedIds.includes(Number(record.id))}
            onClick={() => onToggleSelect(record)}
          />
        )} */
        rowControlExtra={(record) => (
          <UsersTableRowActions
            record={record}
            onRowRestore={
              hasPermission("users.restoreUser") ? onRowRestore : undefined
            }
            onOpenDc={
              hasPermission("dealergraph.user.add") &&
              hasPermission("dealergraph.user.delete")
                ? handleView
                : undefined
            }
            t={t}
          />
        )}
        actionsFixed={true}
        actionsPlacement="left"
        sortField={sortField}
        sortType={normalizedSortType}
        sortableColumns={sortableApiKeys}
        onSortChange={onSortChange}
      />
      <Pagination
        count={Number(count) || 25}
        page={(Number(page) || 0) + 1}
        totalCount={dataUserList?.total}
        onChangeCount={handleCount}
        onChangePage={handlePage}
      />
    </>
  );
};

export default TableUserList;
